import { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/Button";
import { CriticalPath } from "../components/CriticalPath";
import { BrandMarquee } from "../components/BrandMarquee";
import { SectionHead } from "../components/SectionHead";
import { Reveal, stagger, item } from "../lib/motion";
import { CREDENTIALS, GAPS, HOME_SERVICES } from "../data";

const HEADLINE = ["Programme certainty", "for contractors", "without an in-house", "planning team"];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.76, 0, 0.24, 1] } }),
};

export default function Home() {
  const reduce = useReducedMotion();
  useEffect(() => {
    document.title = "PROSPEC — Programme certainty for contractors";
    document.querySelector('meta[name="description"]')?.setAttribute(
      "content",
      "PROSPEC provides outsourced project planning and project-controls services to contractors — baseline programmes, progress tracking, delay analysis and defensible reporting as a monthly retainer."
    );
  }, []);

  return (
    <div className="pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] bg-cover bg-center mix-blend-multiply"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)" }}
        />
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-16 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="eyebrow mb-7"
              >
                Outsourced Planning & Project Controls
              </motion.p>

              <h1 className="font-display font-bold text-ink text-[2.5rem] sm:text-5xl lg:text-[4rem] leading-[1.05]">
                {HEADLINE.map((line, i) => (
                  <span key={i} className="line-mask">
                    <motion.span
                      className="block"
                      custom={i}
                      variants={reduce ? {} : lineVariants}
                      initial={reduce ? { opacity: 0 } : "hidden"}
                      animate={reduce ? { opacity: 1 } : "show"}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.7 }}
                className="mt-8 text-lg md:text-xl text-slate max-w-xl leading-relaxed"
              >
                We build and maintain your baseline, track progress, flag delays early and deliver
                board-ready reporting — a specialist controls function on a monthly retainer, not a
                one-off deliverable or a full PM hire.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.7 }}
                className="mt-10 flex flex-col sm:flex-row gap-4"
              >
                <Button to="/contact" data-testid="hero-cta-primary">
                  Book a free programme health-check <ArrowRight size={16} />
                </Button>
                <Button to="/services" variant="outline" data-testid="hero-cta-secondary">
                  View services
                </Button>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.9 }}
              className="lg:col-span-5"
            >
              <div className="border border-ink/10 bg-cream/60 backdrop-blur-sm p-6 md:p-8">
                <p className="eyebrow mb-6">The critical path, owned</p>
                <CriticalPath />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CREDENTIALS STRIP */}
      <div data-testid="credentials-strip" className="bg-ink text-cream">
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-8">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <span className="eyebrow text-cream/50 shrink-0">Tools & credentials</span>
            {CREDENTIALS.map((c) => (
              <span key={c} className="font-mono text-xs md:text-sm text-cream/85 tracking-wide">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* THE GAP */}
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <SectionHead
          eyebrow="Chapter 01 — The gap"
          title="What happens when no one owns the programme"
          intro="Most contractors do not lack ambition — they lack a dedicated function keeping the schedule honest. Three failures show up again and again."
        />

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid md:grid-cols-3 gap-px bg-ink/10 border border-ink/10 mt-16"
        >
          {GAPS.map((g) => (
            <motion.div key={g.n} variants={item} className="bg-cream p-8 md:p-10">
              <span className="font-mono text-gold text-sm">{g.n}</span>
              <h3 className="font-display text-2xl mt-5 mb-4 leading-tight">{g.title}</h3>
              <p className="text-slate leading-relaxed">{g.body}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Dark callout */}
        <Reveal delay={0.1}>
          <div className="relative grain overflow-hidden bg-ink text-cream mt-px p-10 md:p-16 border border-ink">
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <p className="eyebrow mb-5">What changes with PROSPEC</p>
                <h3 className="font-display text-3xl md:text-4xl leading-tight text-cream">
                  One team owns the programme end to end — building it, keeping it live, and turning
                  it into early warnings and defensible reports you can act on.
                </h3>
              </div>
              <div className="lg:col-span-4 lg:justify-self-end">
                <Button to="/about" variant="ghostLight" data-testid="gap-cta">
                  How we work <ArrowUpRight size={16} />
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <BrandMarquee />

      {/* SERVICES OVERVIEW */}
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <SectionHead
            eyebrow="Chapter 02 — What we do"
            title="Six core controls, one discipline"
            intro="Every engagement is assembled from these building blocks — scoped to your project, delivered on a steady monthly rhythm."
          />
          <Reveal delay={0.2}>
            <Button to="/services" variant="outline" data-testid="services-overview-cta">
              All nine services <ArrowRight size={16} />
            </Button>
          </Reveal>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10"
        >
          {HOME_SERVICES.map((s) => (
            <motion.div key={s.tag} variants={item}>
              <Link
                to="/services"
                data-testid={`home-service-${s.tag}`}
                className="group block bg-cream p-8 md:p-10 h-full transition-all duration-300 hover:bg-sand hover:-translate-y-1"
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-xs text-gold tracking-widest">{s.tag}</span>
                  <ArrowUpRight size={18} className="text-teal opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                </div>
                <h3 className="font-display text-2xl mb-3 leading-tight">{s.title}</h3>
                <p className="text-slate leading-relaxed">{s.body}</p>
                <div className="mt-6 h-px w-10 bg-teal group-hover:w-full transition-all duration-500" />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CLOSING CTA */}
      <ClosingCTA />
    </div>
  );
}

export const ClosingCTA = () => (
  <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
    <Reveal>
      <div className="relative grain overflow-hidden bg-teal text-cream p-12 md:p-20 text-center">
        <div className="relative">
          <p className="eyebrow text-cream/70 mb-6">Start here — no commitment</p>
          <h2 className="font-display font-semibold text-4xl md:text-6xl leading-[1.02] text-cream max-w-4xl mx-auto">
            Book your free programme health-check.
          </h2>
          <p className="text-cream/80 text-lg mt-6 max-w-xl mx-auto">
            We review where your programme stands, show you the risk, and tell you honestly whether
            you need us.
          </p>
          <div className="mt-10">
            <Button to="/contact" data-testid="closing-cta" className="!bg-gold">
              Book a free health-check <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);
